import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import { useAuth, useCan } from '@/auth/AuthContext';
import { useWsEvent } from '@/auth/WsProvider';
import { api } from '@/lib/api';
import { NAV_SECTIONS, isNavItemVisible } from '@/layout/navConfig';
import { useHiddenNavItems } from '@/lib/navPrefs';

function initialsOf(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
}

const ROLE_RU = {
  administrator: 'Администратор',
  teacher: 'Учитель',
  parent: 'Родитель',
  expert: 'Эксперт',
};

/**
 * Счётчик непрочитанных сообщений для бейджа на пункте «Сообщения».
 * Обновляется на каждое `chat.message`-событие из WS (любое сообщение
 * или пометка прочитанным может изменить число) + раз при монтировании.
 */
function useChatUnread(can) {
  const enabled = can('messages', 'read');
  const [unread, setUnread] = useState(0);

  const refresh = () => {
    if (!enabled) return;
    api.get('/api/chats/unread')
      .then((res) => setUnread(Number(res?.unread || 0)))
      .catch(() => {});
  };
  useEffect(refresh, [enabled]);
  useWsEvent('chat.message', refresh);
  return enabled ? unread : 0;
}

export default function Sidebar({ compact }) {
  const { user, logout } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const chatUnread = useChatUnread(can);
  // Персонально скрытые пункты меню (выбор пользователя на /me/settings,
  // хранится в localStorage). Реактивно — сайдбар обновляется сразу.
  const hiddenIds = useHiddenNavItems();
  const hiddenSet = new Set(hiddenIds);
  return (
    <aside className={'sidebar' + (compact ? ' sidebar--compact' : '')}>
      <div className="sidebar__brand">
        <div className="sidebar__logo">
          {/* Брендовый знак школы (PNG из public/img). В свёрнутом сайдбаре
              показываем только эту мини-иконку. */}
          <img src="/img/logo_navbar.png" alt="" className="sidebar__logoImg" />
        </div>
        {!compact && (
          <div className="sidebar__brandText">
            <div className="sidebar__brandName">Новое Поколение</div>
            <div className="sidebar__brandMeta">админ-панель</div>
          </div>
        )}
      </div>

      <nav className="sidebar__nav">
        {NAV_SECTIONS.map((sec) => {
          // Показываем пункт, если он разрешён правами И не скрыт пользователем.
          const visibleItems = sec.items.filter(
            (it) => isNavItemVisible(it, can) && !hiddenSet.has(it.id),
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={sec.title} className="sidebar__section">
              {!compact && <div className="sidebar__sectionTitle">{sec.title}</div>}
              {visibleItems.map((it) => {
                const badge = it.id === 'messages' && chatUnread > 0 ? chatUnread : null;
                return (
                  <NavLink
                    key={it.id}
                    to={it.to}
                    className={({ isActive }) => 'sidebar__item' + (isActive ? ' is-active' : '')}
                    title={compact ? it.label : undefined}
                  >
                    <span className="sidebar__itemIcon"><Icon name={it.icon} size={15} /></span>
                    {!compact && <span className="sidebar__itemLabel">{it.label}</span>}
                    {badge !== null && (
                      <span className="sidebar__itemBadge" title="Непрочитанные">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="sidebar__foot">
        <div
          className="sidebar__user"
          onClick={() => navigate('/me/settings')}
          role="button"
          title="Мои настройки"
        >
          <div className="sidebar__avatar">{initialsOf(user?.name)}</div>
          {!compact && (
            <div className="sidebar__userText">
              <div className="sidebar__userName">{user?.name || '—'}</div>
              <div className="sidebar__userRole">{ROLE_RU[user?.role] || user?.role}</div>
            </div>
          )}
          {!compact && (
            <button
              className="sidebar__userMore"
              title="Выйти"
              onClick={(e) => { e.stopPropagation(); logout(); }}
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
